/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useState } from 'react';

import moment from 'moment-timezone';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import deferToClientSideRender from 'js/lib/deferToClientSideRender';
import user from 'js/lib/user';
import { hasEqualUTCOffset } from 'js/utils/DateTimeUtils';

import { breakpoints } from '@coursera/cds-common';
import { Button, PageNotification } from '@coursera/cds-core';

import localStorageEx from 'bundles/common/utils/localStorageEx';

import _t from 'i18n!nls/page';

// Sits above page content but below CDS tooltips/true modals, matching @coursera/cds-common's zIndex.snackbar.
const NOTIFICATION_Z_INDEX = 10100;

const styles = {
  container: css`
    position: fixed;
    z-index: ${NOTIFICATION_Z_INDEX};
    top: var(--cds-spacing-1000);
    right: var(--cds-spacing-200);
    width: 400px;
    max-width: calc(100vw - var(--cds-spacing-400));

    ${breakpoints.down('sm')} {
      left: var(--cds-spacing-200);
      width: auto;
      max-width: none;
    }
  `,
  actions: css`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--cds-spacing-150);
  `,
};

const TimezoneMismatchNotification = ({ isItemPage = false }) => {
  const [hasDismissedModal, setHasDismissedModal] = useState(false);
  const courseraSettingsTimezone = user.get().timezone;
  const localTimezone = moment.tz.guess();
  const areUTCOffsetsEqual = hasEqualUTCOffset(courseraSettingsTimezone, localTimezone);

  const shouldHidePopupBasedOnBehavior =
    areUTCOffsetsEqual ||
    hasDismissedModal ||
    localStorageEx.getItem('ignoreTimezoneMismatch', (value) => value === 'true', false);
  const shouldHidePopupBecauseOfPageType = isItemPage;
  if (shouldHidePopupBasedOnBehavior || shouldHidePopupBecauseOfPageType) {
    return null;
  }

  const handleDismiss = () => {
    setHasDismissedModal(true);
  };

  const handleDoNotShowAgain = () => {
    localStorageEx.setItem('ignoreTimezoneMismatch', 'true', (value) => value);
    setHasDismissedModal(true);
  };

  return (
    <div className="rc-TimezoneMismatchNotification" css={styles.container} data-testid="timezone-modal-content">
      <PageNotification
        severity="warning"
        title={_t('Timezone mismatch')}
        onDismiss={handleDismiss}
        actions={
          <div css={styles.actions}>
            <Button
              variant="secondary"
              size="small"
              component="a"
              href="/account-settings"
              target="_blank"
              rel="noopener"
              aria-label={_t('Change timezone setting')}
            >
              {_t('Update')}
            </Button>
            <Button
              variant="ghost"
              size="small"
              component="div"
              onClick={handleDoNotShowAgain}
              aria-label={_t("Don't show the timezone mismatch notification again")}
            >
              {_t('Ignore')}
            </Button>
          </div>
        }
      >
        <FormattedMessage
          message={_t('Deadlines may show in the wrong timezone. Your account is set to {courseraSettingsTimezone}.')}
          courseraSettingsTimezone={courseraSettingsTimezone}
        />
      </PageNotification>
    </div>
  );
};

export default deferToClientSideRender(TimezoneMismatchNotification);
